import { Box, useTheme } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery } from '@tanstack/react-query';
import { DatabaseTable } from '../../../shared/components/ui/DatabaseTable';
import { ActionMenu, type ActionMenuItem } from '../../../shared/components/ActionMenu';
import { useLocker } from '../context/useLocker';
import { LockerErrorHandler } from '../components/LockerList/LockerErrorHandler';
import { getLockerColumns } from '../components/LockerList/LockerTableConfig';
import { LockerListHeader } from '../components/LockerList/LockerListHeader';
import { lockerReservationService } from '../../../shared/services/lockers/lockerReservationService';
import { ModalDelete } from '../components/ModalDelete';
import type { Locker } from '../context/LockerContext';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ModalBlock } from '../components/LockerBlockMaintenance/ModalBlock';
import { ModalUnblock } from '../components/LockerUnBlock/ModalUnblock';

export default function LockerList() {
  const theme = useTheme();
  const { 
    lockers, 
    isLoading, 
    error, 
    handleCreate, 
    handleView, 
    handleCloseError,
    handleCreateReservation,
    isDeleteReservationModalOpen,
    openDeleteReservationModal,
    closeDeleteReservationModal,
    handleDeleteReservation,
    isDeletingReservation,
    openBlockModal,
    isBlockModalOpen,
    closeBlockModal,
    handleBlockLocker,
    isBlocking,
    openUnblockModal,
    isUnblockModalOpen,
    closeUnblockModal,
    handleUnblockLocker,
    isUnblocking,
    selectedLocker
  } = useLocker();

  // Buscar todas as reservas para verificar quais lockers têm reservas
  const { data: allReservations = [] } = useQuery({
    queryKey: ['locker-reservations'],
    queryFn: async () => {
      try {
        const response = await lockerReservationService.list();
        return response;
      } catch (error) {
        console.error('Erro ao buscar reservas:', error);
        return [];
      }
    }
  });

  const renderCustomActions = (row: any) => {
    const locker = row as Locker;
    
    // Verificar se existe reserva para este locker
    const hasReservation = allReservations.some((reservation: any) => 
      reservation.lockerId === locker.id || reservation.equipmentId === locker.equipmentId
    );

    // Encontrar a reserva específica se existir
    const currentReservation = allReservations.find((reservation: any) => 
      reservation.lockerId === locker.id || reservation.equipmentId === locker.equipmentId
    );

    const menuItems: ActionMenuItem[] = [
      {
        label: 'Visualizar',
        icon: <VisibilityIcon fontSize="small" />,
        onClick: () => handleView(locker)
      }
    ];

    if (hasReservation && currentReservation) {
      // Se tem reserva, mostrar opções de editar e excluir reserva
      menuItems.push(
        {
          label: 'Alterar Reserva',
          icon: <EditIcon fontSize="small" />,
          onClick: () => {
            // Navegar para a página de edição da reserva
            window.location.href = `/lockers/reservation/${currentReservation.id}/edit`;
          }
        },
        {
          label: 'Excluir Reserva',
          icon: <DeleteIcon fontSize="small" />,
          onClick: () => {
            openDeleteReservationModal(currentReservation);
          }
        }
      );
    } else {
      // Se não tem reserva, mostrar opção de criar reserva
      menuItems.push({
        label: 'Criar Reserva',
        icon: <CalendarMonthIcon fontSize="small" />,
        onClick: () => handleCreateReservation(locker)
      });
    }

    // Adicionar opções de bloqueio/desbloqueio baseado no status atual
    if (locker.is_available) {
      // Se está disponível, mostrar opção de indisponibilizar
      menuItems.push({
        label: 'Indisponibilizar armário',
        icon: <BuildIcon fontSize="small" />,
        onClick: () => {
          openBlockModal(locker);
        }
      });
    } else {
      // Se está indisponível, mostrar opção de disponibilizar
      menuItems.push({
        label: 'Disponibilizar armário',
        icon: <CheckCircleIcon fontSize="small" />,
        onClick: () => {
          openUnblockModal(locker);
        }
      });
    }

    return (
      <ActionMenu 
        items={menuItems}
        color={theme.palette.text.secondary}
      />
    );
  };

  return (
    <>
      <LockerErrorHandler 
        error={error} 
        onClose={handleCloseError} 
      />

      {/* Modal para deletar reserva */}
      <ModalDelete
        open={isDeleteReservationModalOpen}
        onClose={closeDeleteReservationModal}
        onConfirm={handleDeleteReservation}
        loading={isDeletingReservation}
        title="Excluir Reserva"
        description="Tem certeza que deseja excluir esta reserva? Esta ação não poderá ser desfeita."
      />

      <ModalBlock
        open={isBlockModalOpen}
        onClose={closeBlockModal}
        onConfirm={handleBlockLocker}
        loading={isBlocking}
        title={`Indisponibilizar armário - ${selectedLocker?.name}`}
        description="Tem certeza que deseja indisponibilizar este armário? Este armário ficará indisponível para reservas até a manutenção ser concluída."
      />

      <ModalUnblock
        open={isUnblockModalOpen}
        onClose={closeUnblockModal}
        onConfirm={handleUnblockLocker}
        loading={isUnblocking}
        title={`Disponibilizar armário - ${selectedLocker?.name}`}
        description="Tem certeza que deseja disponibilizar este armário? Este armário ficará disponível para novas reservas."
      />

      <Box style={{ 
        background: theme.palette.background.paper, 
        borderRadius: 16, 
        padding: 32, 
        boxShadow: '0 2px 8px 0 rgba(16,30,54,0.04)', 
        maxWidth: '100%', 
        margin: '0 auto' 
      }}>
        <LockerListHeader 
          isLoading={isLoading}
          onCreate={handleCreate}
        />
        
        <DatabaseTable
          rows={lockers}
          columns={getLockerColumns()}
          loading={isLoading}
          renderCustomActions={renderCustomActions}
          hideDelete
          hideEdit
          hideView
        />
      </Box>
    </>
  );
}